import { useCCF } from '@app/ccf/company-report/ccf-context';
import { purchasedGoodsRoutes } from '@app/purchased-goods/purchased-goods-routes';
import { useGetPackagingActivityList } from '@shared/api/hooks/ccf/activities/packaging';
import {
  CompanyReportsActivitiesPackagingListSortItem,
  PackagingActivitiesList,
  PaginatedPackagingActivitiesListListFilters,
} from '@shared/api/types';
import { DataTable } from '@shared/components/data-table/data-table';
import { DataTableToolbar } from '@shared/components/data-table/data-table-toolbar';
import { useServerDataTable } from '@shared/components/data-table/hooks/use-server-data-table';
import { baseTableSchema } from '@shared/components/data-table/schema';
import {
  DataTableFilterField,
  DataTableProps,
} from '@shared/components/data-table/types';
import { useEffect, useMemo, useState } from 'react';
import {
  generatePath,
  useLocation,
  useNavigate,
  useSearchParams,
} from 'react-router-dom';
import { z } from 'zod';
import { usePackagingTableColumns } from './packaging-table-columns';
import PackagingTableToolbar from './packaging-table-toolbar';

interface PackagingTableProps extends Partial<DataTableProps> {}

export const schema = baseTableSchema.extend({
  packagingName: z.string().optional(),
});

export default function CCFPackagingTable({
  enableRowClick = true,
}: PackagingTableProps) {
  const [filters, setFilters] =
    useState<PaginatedPackagingActivitiesListListFilters>({});

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const search = schema.parse(Object.fromEntries(searchParams));
  const { uuid: reportUuid, timePeriod } = useCCF();

  const supplier = searchParams.get('supplier')?.split('.');
  const period = searchParams.get('period')?.split('.');
  const sites = searchParams.get('site')?.split('.');

  const { data, isPreviousData, isFetchedAfterMount, isLoading, isError } =
    useGetPackagingActivityList(reportUuid!, {
      page: search.page,
      page_size: search.page_size,
      suppliers: supplier,
      time_periods: period,
      search: search.packagingName || '',
      sites: sites,
      sort: [search.sort] as CompanyReportsActivitiesPackagingListSortItem[],
    });

  if (isError) throw new Error();

  const { results: activities, count } = data || {};

  const pageCount = count ? Math.ceil(count / (search.page_size || 10)) : 0;

  const useFilterOptions = useMemo(() => {
    const supplierFilter = filters.suppliers?.map((filter) => {
      return {
        label: filter.name,
        value: filter.uuid,
      };
    });

    const timePeriodFilter = filters.timePeriods?.map((filter) => {
      return {
        label: filter.name,
        value: filter.uuid,
      };
    });

    const siteFilter = filters.sites?.map((filter) => {
      return {
        label: filter.name,
        value: filter.uuid,
      };
    });

    return {
      suppliers: supplierFilter || [],
      timePeriods: timePeriodFilter || [],
      sites: siteFilter || [],
    };
  }, [filters]);

  useEffect(() => {
    if (data && !isPreviousData && isFetchedAfterMount) {
      setFilters({
        suppliers: data.filters?.suppliers,
        timePeriods: data.filters?.timePeriods,
        sites: data.filters?.sites,
      });
    }
  }, [data, isFetchedAfterMount, isPreviousData]);

  const filterFields: DataTableFilterField<PackagingActivitiesList>[] = [
    {
      label: 'Name',
      value: 'packagingName',
      placeholder: 'Search item...',
    },
    {
      label: 'Supplier',
      value: 'supplier' as keyof PackagingActivitiesList,
      options: useFilterOptions.suppliers,
    },
    {
      label: 'Time period',
      value: 'period',
      options: useFilterOptions.timePeriods,
    },
    {
      label: 'Site',
      value: 'site',
      options: useFilterOptions.sites,
    },
  ];

  const packagingTableColumns = usePackagingTableColumns();

  const { table, context } = useServerDataTable({
    data: activities || [],
    columns: packagingTableColumns,
    pageCount,
    filterFields,
    defaultPerPage: 10,
    rowId: 'uuid',
    columnVisibilityState: {
      uuid: false,
      period: false,
      site: false,
      remoteId: false,
    },
  });

  return (
    <>
      <DataTable
        table={table}
        context={context}
        enablePagination
        className="bg-gray-00"
        rowClassName={() =>
          enableRowClick ? 'cursor-pointer hover:bg-gray-50' : 'cursor-default'
        }
        onRowClick={(row) => {
          if (!enableRowClick) return;
          navigate(
            generatePath(purchasedGoodsRoutes.PURCHASED_PACKAGING_ITEM, {
              item_uuid: row?.original.uuid,
            }) + `?periodStart=${timePeriod}&periodEnd=${timePeriod}`
          );
        }}
        isLoading={(!isFetchedAfterMount && isPreviousData) || isLoading}
      >
        <DataTableToolbar table={table} filterFields={filterFields}>
          <PackagingTableToolbar />
        </DataTableToolbar>
      </DataTable>
    </>
  );
}
